import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Select, Button, Typography, Space, Tag, Alert } from 'antd';
import { DatePicker } from 'Components';
import {
  CalendarOutlined,
  SaveOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getStoredWorkMonth, setStoredWorkMonth } from 'utils/workMonthStorage';

const { Text, Paragraph } = Typography;
const { Option } = Select;

const WorkMonthConfig: React.FC = () => {
  const navigate = useNavigate();

  // Đọc giá trị kỳ làm việc hiện tại
  const [activeMonth, setActiveMonth] = useState<number>(() => getStoredWorkMonth().month);
  const [activeYear, setActiveYear] = useState<number>(() => getStoredWorkMonth().year);

  // Form input tạm
  const [formMonth, setFormMonth] = useState<number>(() => getStoredWorkMonth().month);
  const [formYear, setFormYear] = useState<dayjs.Dayjs | null>(() =>
    dayjs(`${getStoredWorkMonth().year}`, 'YYYY')
  );

  // Lắng nghe thay đổi nếu có tab khác cập nhật
  useEffect(() => {
    const handleSync = (e: any) => {
      if (e?.detail) {
        setActiveMonth(e.detail.month);
        setActiveYear(e.detail.year);
        setFormMonth(e.detail.month);
        setFormYear(dayjs(`${e.detail.year}`, 'YYYY'));
      }
    };
    window.addEventListener('workMonthChanged', handleSync);
    return () => window.removeEventListener('workMonthChanged', handleSync);
  }, []);

  const handleSave = () => {
    const selectedYear = formYear ? formYear.year() : activeYear;
    setStoredWorkMonth(formMonth, selectedYear);
    setActiveMonth(formMonth);
    setActiveYear(selectedYear);
    toast.success(`Đã cập nhật kỳ làm việc thành công: Tháng ${formMonth}/${selectedYear}`);
  };

  const handleResetToCurrentCalendar = () => {
    const now = new Date();
    const curMonth = now.getMonth() + 1;
    const curYear = now.getFullYear();
    setFormMonth(curMonth);
    setFormYear(dayjs(`${curYear}`, 'YYYY'));
    setStoredWorkMonth(curMonth, curYear);
    setActiveMonth(curMonth);
    setActiveYear(curYear);
    toast.info(`Đã đặt lại kỳ làm việc về thời gian thực: Tháng ${curMonth}/${curYear}`);
  };

  const isChanged =
    formMonth !== activeMonth || (formYear && formYear.year() !== activeYear);

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto', padding: '16px 8px' }}>
      <Row gutter={[20, 20]}>
        {/* Cột trái: Form cấu hình kỳ làm việc */}
        <Col xs={24} md={14}>
          <Card
            title={
              <Space>
                <CalendarOutlined style={{ color: '#1890ff', fontSize: 18 }} />
                <span style={{ fontSize: 16, fontWeight: 600 }}>Cấu hình kỳ làm việc</span>
              </Space>
            }
            bordered={false}
            style={{
              borderRadius: 8,
              boxShadow: '0 1px 4px rgba(0, 21, 41, 0.08)',
            }}
          >
            {/* Banner trạng thái hiện tại */}
            <div
              style={{
                background: '#f0f5ff',
                border: '1px solid #adc6ff',
                borderRadius: 8,
                padding: '14px 18px',
                marginBottom: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div>
                <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                  KỲ LÀM VIỆC HIỆN TẠI ĐANG ÁP DỤNG
                </Text>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#1d39c4' }}>
                  Tháng {activeMonth} / Năm {activeYear}
                </div>
              </div>
              <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 13, padding: '4px 10px' }}>
                Đang hoạt động
              </Tag>
            </div>

            {/* Bộ chọn tháng và năm */}
            <div style={{ marginBottom: 20 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
                Chọn tháng làm việc:
              </Text>
              <Select
                value={formMonth}
                onChange={(m) => setFormMonth(m)}
                style={{ width: '100%', maxWidth: 280 }}
                size="large"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <Option key={m} value={m}>
                    Tháng {m}
                  </Option>
                ))}
              </Select>
            </div>

            <div style={{ marginBottom: 28 }}>
              <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 8 }}>
                Chọn năm làm việc:
              </Text>
              <DatePicker
                picker="year"
                value={formYear}
                onChange={(dt) => setFormYear(dt)}
                allowClear={false}
                format="YYYY"
                placeholder="Chọn năm"
                style={{ width: '100%', maxWidth: 280 }}
                size="large"
              />
            </div>

            <Space size={12} wrap>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                size="large"
                onClick={handleSave}
                style={{ minWidth: 140, fontWeight: 500 }}
              >
                Lưu cấu hình
              </Button>
              <Button
                icon={<ReloadOutlined />}
                size="large"
                onClick={handleResetToCurrentCalendar}
              >
                Đặt về tháng hiện tại
              </Button>
            </Space>

            {isChanged && (
              <Alert
                message="Bạn đang có thay đổi chưa lưu. Hãy bấm 'Lưu cấu hình' để áp dụng."
                type="warning"
                showIcon
                style={{ marginTop: 20 }}
              />
            )}
          </Card>
        </Col>

        {/* Cột phải: Hướng dẫn & Liên kết nhanh */}
        <Col xs={24} md={10}>
          <Space direction="vertical" size={20} style={{ width: '100%' }}>
            {/* Thẻ hướng dẫn */}
            <Card
              title={
                <Space>
                  <InfoCircleOutlined style={{ color: '#52c41a' }} />
                  <span style={{ fontSize: 15, fontWeight: 600 }}>Thông tin hướng dẫn</span>
                </Space>
              }
              bordered={false}
              style={{
                borderRadius: 8,
                boxShadow: '0 1px 4px rgba(0, 21, 41, 0.08)',
              }}
            >
              <Paragraph style={{ color: '#595959', fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
                Kỳ làm việc được thiết lập độc lập cho tài khoản kế toán trên trình duyệt của trường hiện tại.
              </Paragraph>
              <Paragraph style={{ color: '#595959', fontSize: 13, lineHeight: 1.6, marginBottom: 12 }}>
                Khi bạn thay đổi kỳ làm việc tại đây, hệ thống sẽ <strong>tự động áp dụng</strong> tháng/năm này cho các nghiệp vụ:
              </Paragraph>
              <ul style={{ paddingLeft: 18, color: '#595959', fontSize: 13, lineHeight: 1.8 }}>
                <li>Bảng đăng ký nguồn thu theo lớp</li>
                <li>Đăng ký nhanh nguồn thu theo khối</li>
                <li>Lập biên lai và quản lý công nợ</li>
              </ul>
            </Card>

            {/* Thẻ thao tác nhanh */}
            <Card
              title={<span style={{ fontSize: 15, fontWeight: 600 }}>Truy cập nhanh</span>}
              bordered={false}
              style={{
                borderRadius: 8,
                boxShadow: '0 1px 4px rgba(0, 21, 41, 0.08)',
              }}
            >
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <Button
                  block
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate('/class-year-income')}
                  style={{ textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  Bảng đăng ký nguồn thu theo lớp
                </Button>
                <Button
                  block
                  icon={<ArrowRightOutlined />}
                  onClick={() => navigate('/quick-register')}
                  style={{ textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                  Đăng ký nhanh nguồn thu
                </Button>
              </Space>
            </Card>
          </Space>
        </Col>
      </Row>
    </div>
  );
};

export default WorkMonthConfig;
